import { type FC, type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';
import { captureException } from '@sentry/remix';
import cx from 'classnames';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { COMMUNITY_FEED_OVERLAY_OFFSET_CLASS } from '~/constants/community';
import useIsMobile from '~/hooks/useIsMobile';
import { useNectarImpression } from '~/hooks/useNectarImpression';
import type { RootLoaderData } from '~/root';
import type { NectarPageBlock } from '~/utils/nectarPageBlock';

import { POPOVER_PANEL_CLASSES } from '../ui/_styles/popover';
import { BottomSheetDialog } from '../ui/BottomSheetDialog';
import { Dialog } from '../ui/Dialog';
import { ModalBackButton } from '../ui/ModalBackButton';
import { ModalBrandHeader } from '../ui/ModalBrandHeader';
import { ModalCloseButton } from '../ui/ModalCloseButton';
import { PillButton } from '../ui/PillButton';

import { useCommunityDisplayName } from '../../_hooks/useCommunityDisplayName';
import { useCommunityLogoUrl } from '../../_hooks/useCommunityLogoUrl';
import type { CommunityPurchasableTiers } from '../../_hooks/useCommunityPurchasableTiers';
import { AwaitingAccessStep } from './_components/AwaitingAccessStep';
import { PricingStep } from './_components/PricingStep';
import type { CheckAccess } from './useAccessPoll';

type UpgradeModalStep = 'plans' | 'payment' | 'awaiting-access' | 'granted';

interface AccessStepsContent {
  /** Shown under the payment processing title while the access poll runs; the flag says the poll gave up. */
  awaiting: { description: string; body: (isTimedOut: boolean) => ReactNode };
  /** Step shown once access lands; its button and the close button run the handoff. */
  granted: { title: string; description: string; body: ReactNode; ctaLabel: string };
}

// z-[70] to stack above other community overlays using z-50
const MODAL_Z_INDEX_CLASS = 'z-[70]';
const MODAL_BACKDROP_CLASS = 'bg-black/70';

interface BaseUpgradeModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  /** Adds the granted step after access lands; without it the modal hands off as soon as the poll passes. */
  accessStepsContent?: AccessStepsContent;
  /** Impression block for this modal, so community and channel views are counted apart. */
  impressionPageBlock: NectarPageBlock;
  pricing: CommunityPurchasableTiers;
  checkAccess: CheckAccess;
  isAwaitingAccess: boolean;
  /** Receives the tier that was checked out, when the pricing step could read it. */
  onPaymentAccepted: (tierId: string | undefined) => void;
  onAccessGranted: () => void;
  successRedirectTo: string;
  onGuestSuccess?: (ssoLoginUrl?: string) => void;
  footer?: ReactNode;
  resumeAwaitingAccessOnOpen?: boolean;
}

/**
 * Dialog shell and step machine shared by the community and channel upgrade modals:
 * plans -> payment -> awaiting access -> granted (only with `accessStepsContent`).
 *
 * Wrappers mount it only while open, so the initial step is derived once at mount
 * and closing resets everything. What outlives the modal — the awaiting-access latch
 * and the post-purchase handoff — belongs to the layout hook behind
 * `isAwaitingAccess` / `onPaymentAccepted` / `onAccessGranted`.
 */
export const BaseUpgradeModal: FC<BaseUpgradeModalProps> = ({
  isOpen,
  title,
  description,
  accessStepsContent,
  impressionPageBlock,
  pricing,
  checkAccess,
  isAwaitingAccess,
  onPaymentAccepted,
  onAccessGranted,
  successRedirectTo,
  onGuestSuccess,
  footer,
  resumeAwaitingAccessOnOpen,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const isLoggedIn = Boolean(rootData?.isLoggedIn);
  const { handleCloseModal } = useGlobalModals();
  const communityName = useCommunityDisplayName();
  const communityLogoUrl = useCommunityLogoUrl();

  // Only a logged-in viewer starts awaiting access: OTP return, or reopening after paying in place.
  const startsAwaitingAccess = isLoggedIn && (resumeAwaitingAccessOnOpen || isAwaitingAccess);
  const [step, setStep] = useState<UpgradeModalStep>(() => (startsAwaitingAccess ? 'awaiting-access' : 'plans'));

  const handleClose = () => {
    // Access already landed: closing early must still run the handoff, or the page stays gated behind the closed modal.
    if (step === 'granted') {
      onAccessGranted();
      return;
    }
    handleCloseModal({ preventScrollReset: true });
  };

  const handlePollGranted = () => {
    if (!accessStepsContent) {
      onAccessGranted();
      return;
    }
    setStep('granted');
  };

  // The access poll can't see an anonymous buyer, so the wrapper signs a guest in. A wrapper
  // that admits no guests passes no handler; a guest paying there must not fail silently.
  const handleGuestSuccess = (ssoLoginUrl?: string) => {
    if (onGuestSuccess) {
      onGuestSuccess(ssoLoginUrl);
      return;
    }
    captureException(new Error('Upgrade modal: a guest paid but the modal has no guest handler'), {
      extra: { title, ssoLoginUrl: Boolean(ssoLoginUrl) },
    });
  };

  const handleSuccess = (ssoLoginUrl?: string, tierId?: string) => {
    if (ssoLoginUrl || !isLoggedIn) {
      handleGuestSuccess(ssoLoginUrl);
      return;
    }

    onPaymentAccepted(tierId);
    setStep('awaiting-access');
  };

  const isPlansStep = step === 'plans';
  // Nothing sellable (empty or failed fetch): render the closed-community message instead of an empty grid.
  const nothingSellable =
    pricing.isError || (pricing.purchasableTiers !== null && pricing.purchasableTiers.length === 0);
  const showsClosedMessage = isPlansStep && nothingSellable;

  const panelWidthClass = useMemo(() => {
    // Opens at the auth modals' width, then caps at each tier count's single-row width
    // (N*card + (N-1)*24 gap + sm:px-12 padding).
    const tierCount = pricing.purchasableTiers?.length ?? 0;
    // Below lg cards stack at the compact width; from lg up small catalogs never get a mostly-empty panel.
    const PRICING_WIDTH_BY_COUNT: Record<number, string> = {
      1: 'max-w-[460px] xl:max-w-[480px]',
      2: 'max-w-[460px] lg:max-w-[760px] xl:max-w-[888px]',
      3: 'max-w-[460px] lg:max-w-[1104px] xl:max-w-[1296px]',
    };
    const wideWidth = 'max-w-[460px] lg:max-w-[1448px] xl:max-w-[1704px]';

    return isPlansStep && tierCount > 0 ? (PRICING_WIDTH_BY_COUNT[tierCount] ?? wideWidth) : 'max-w-[460px]';
  }, [pricing.purchasableTiers, isPlansStep]);

  // Show dialog after its final size is known, to avoid jumps while data is resolved.
  const showDialog = isOpen && (step !== 'plans' || !pricing.isPending);

  // One view per browsing session, counted only when the modal actually shows; source CTA segmentable via _bhnpb.
  useNectarImpression({ pageBlock: impressionPageBlock }, { enabled: showDialog });

  const isPaymentStep = step === 'payment';
  const isGrantedStep = step === 'granted';

  const stepHeaders: Partial<Record<UpgradeModalStep, { title: string; description?: string }>> = {
    'awaiting-access': {
      title: t('community.upgrade_modal.payment_processing'),
      description: accessStepsContent?.awaiting.description,
    },
    granted: accessStepsContent?.granted,
  };
  const header = showsClosedMessage
    ? { title: t('community.paid_gate.closed_title') }
    : (stepHeaders[step] ?? { title, description });

  // Fixed footer: always-on Next on the plan step (the provider pre-selects a plan), the handoff on the granted step
  const actionBar = ((isPlansStep && !showsClosedMessage) || isGrantedStep) && (
    <div className="shrink-0 border-t border-black/10 px-6 py-4 sm:px-12">
      <div className="mx-auto w-full max-w-md">
        <PillButton
          variant="primary"
          size="lg"
          fullWidth
          type="button"
          onClick={isGrantedStep ? onAccessGranted : () => setStep('payment')}
        >
          <span className="min-w-0 truncate">
            {isGrantedStep ? accessStepsContent?.granted.ctaLabel : t('community.upgrade_modal.next')}
          </span>
        </PillButton>
      </div>
    </div>
  );

  const content = (
    <>
      <ModalBrandHeader
        className="mb-8"
        imageUrl={communityLogoUrl}
        imageAlt={`${communityName} logo`}
        title={header.title}
        description={header.description}
      />
      {showsClosedMessage ? (
        <p className="text-center text-sm leading-5 text-black/60">{t('community.paid_gate.closed_description')}</p>
      ) : step === 'awaiting-access' ? (
        <AwaitingAccessStep onAccessGranted={handlePollGranted} checkAccess={checkAccess}>
          {accessStepsContent?.awaiting.body}
        </AwaitingAccessStep>
      ) : isGrantedStep ? (
        accessStepsContent?.granted.body
      ) : (
        <PricingStep
          onSuccess={handleSuccess}
          visibleSection={step === 'payment' ? 'checkout' : 'tiers'}
          pricing={pricing}
          successRedirectTo={successRedirectTo}
        />
      )}
      {!isPaymentStep && footer}
    </>
  );

  if (isMobile) {
    return (
      <BottomSheetDialog
        isOpen={showDialog}
        onClose={handleClose}
        zIndexClassName={MODAL_Z_INDEX_CLASS}
        backdropClassName={MODAL_BACKDROP_CLASS}
        panelClassName="flex max-h-[85dvh] flex-col overflow-hidden"
      >
        <div className="flex h-14 shrink-0 items-center justify-between py-2 pl-2 pr-2">
          <div>
            {isPaymentStep && (
              <ModalBackButton
                onBack={() => setStep('plans')}
                label={t('community.upgrade_modal.back')}
                className="p-2"
              />
            )}
          </div>
          <ModalCloseButton onClose={handleClose} label={t('community.upgrade_modal.close')} className="p-2" />
        </div>
        <div className="flex flex-col overflow-y-auto px-5 pb-8">{content}</div>
        {actionBar}
      </BottomSheetDialog>
    );
  }

  return (
    <Dialog
      isOpen={showDialog}
      onClose={handleClose}
      zIndexClassName={MODAL_Z_INDEX_CLASS}
      backdropClassName={MODAL_BACKDROP_CLASS}
      containerClassName={COMMUNITY_FEED_OVERLAY_OFFSET_CLASS}
      className={cx(
        POPOVER_PANEL_CLASSES,
        'relative flex max-h-[85vh] w-full flex-col overflow-hidden border-0',
        panelWidthClass,
      )}
    >
      {isPaymentStep && (
        <ModalBackButton
          onBack={() => setStep('plans')}
          label={t('community.upgrade_modal.back')}
          className="absolute left-4 top-4 z-10 size-8"
        />
      )}
      <ModalCloseButton
        onClose={handleClose}
        label={t('community.upgrade_modal.close')}
        className="absolute right-4 top-4 z-10 size-8"
      />
      <div className={cx('flex flex-col overflow-y-auto px-6 py-8', isPaymentStep ? 'sm:px-8' : 'sm:px-12')}>
        {content}
      </div>
      {actionBar}
    </Dialog>
  );
};
